import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { getTeamUuid } from '../../../../utils/cookies';
import { estimatePipelineCost, createResearchPipeline } from '../../../../Requests/ResearchPipelineRequests';
import { getProjects } from '../../../../Requests/ProjectRequests';
import { useMyContext } from '../../../../Components/ContextProviders/CreditsContextProvider';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import PipelineCreationForm from '../../../../Components/ResearchPipeline/PipelineCreationForm';
import CostEstimationModal from '../../../../Components/ResearchPipeline/CostEstimationModal';
import NoCreditsModal from '../../../../Components/Billing/NoCreditsModal';
import ParticleLoader from "../../../../Components/ParticleLoader";

function CreateResearchPipeline() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { credits, setCredits } = useMyContext();
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [costEstimate, setCostEstimate] = useState(null);
    const [showCostModal, setShowCostModal] = useState(false);
    const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
    const [formData, setFormData] = useState(null);

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            const teamUuid = getTeamUuid();
            const response = await getProjects(teamUuid);
            if (response.data.success) {
                setProjects(response.data.projects || []);
            }
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    };

    const handleFormSubmit = async (data) => {
        setFormData(data);
        setLoading(true);

        try {
            // Get cost estimation first
            const estimateResponse = await estimatePipelineCost({
                team_uuid: getTeamUuid(),
                file_count: data.files.length,
                estimated_file_duration: 30 // Default estimation
            });

            if (estimateResponse.data.success) {
                setCostEstimate(estimateResponse.data);
                
                if (!estimateResponse.data.sufficient_credits) {
                    setShowInsufficientCreditsModal(true);
                } else {
                    setShowCostModal(true);
                }
            } else {
                throw new Error(t('ResearchPipeline.failedCostEstimation'));
            }
        } catch (error) {
            console.error('Error getting cost estimate:', error);
            alert(t('ResearchPipeline.failedCostEstimationTryAgain'));
        } finally {
            setLoading(false);
        }
    };

    const handleCostConfirmation = async () => {
        setShowCostModal(false);
        setLoading(true);

        try {
            const submitData = new FormData();
            
            // Add basic fields
            submitData.append('team_uuid', getTeamUuid());
            // Only add project_uuid if using existing project
            if (formData.useExistingProject && formData.projectUuid) {
                submitData.append('project_uuid', formData.projectUuid);
            }
            submitData.append('name', formData.name);
            submitData.append('research_description', formData.researchDescription);
            
            // Add article requirements as separate fields
            submitData.append('article_requirements[title]', formData.articleRequirements.title);
            submitData.append('article_requirements[pages]', formData.articleRequirements.pages);
            submitData.append('article_requirements[bibliography_format]', formData.articleRequirements.bibliography_format);
            submitData.append('article_requirements[language]', formData.articleRequirements.language);
            
            // Add article description as a top-level field (matching backend expectations)
            if (formData.articleRequirements.description && formData.articleRequirements.description.trim()) {
                submitData.append('article_description', formData.articleRequirements.description.trim());
            }
            
            // Add optional article prompt as a top-level field (moved out of article_requirements)
            if (formData.articleRequirements.articlePrompt && formData.articleRequirements.articlePrompt.trim()) {
                submitData.append('article_prompt', formData.articleRequirements.articlePrompt.trim());
            }
            
            // Add research context fields as top-level fields (matching backend expectations)
            if (formData.researchContext) {
                if (formData.researchContext.researchQuestion && formData.researchContext.researchQuestion.trim()) {
                    submitData.append('research_question', formData.researchContext.researchQuestion.trim());
                }
                if (formData.researchContext.importanceRationale && formData.researchContext.importanceRationale.trim()) {
                    submitData.append('importance_rationale', formData.researchContext.importanceRationale.trim());
                }
                if (formData.researchContext.participantsSelection && formData.researchContext.participantsSelection.trim()) {
                    submitData.append('participants_selection', formData.researchContext.participantsSelection.trim());
                }
                if (formData.researchContext.dataCollectionMethods && formData.researchContext.dataCollectionMethods.trim()) {
                    submitData.append('data_collection_methods', formData.researchContext.dataCollectionMethods.trim());
                }
                if (formData.researchContext.dataAnalysisMethods && formData.researchContext.dataAnalysisMethods.trim()) {
                    submitData.append('data_analysis_methods', formData.researchContext.dataAnalysisMethods.trim());
                }
                if (formData.researchContext.limitations && formData.researchContext.limitations.trim()) {
                    submitData.append('limitations', formData.researchContext.limitations.trim());
                }
            }
            
            // Add fallback search terms as array
            const validSearchTerms = formData.fallbackSearchTerms.filter(term => term.trim());
            validSearchTerms.forEach((term, index) => {
                submitData.append(`fallback_search_terms[${index}]`, term.trim());
            });
            
            // Add files
            formData.files.forEach((file, index) => {
                submitData.append(`files[${index}]`, file);
            });

            // Debug: Log form data being sent (remove in production)
            console.log('Form data being sent:');
            for (let [key, value] of submitData.entries()) {
                console.log(key + ': ' + (value instanceof File ? `File: ${value.name}` : value));
            }

            const response = await createResearchPipeline(submitData);
            
            if (response.data.success) {
                // Update credits
                const newCredits = credits - costEstimate.estimated_cost;
                setCredits(newCredits);
                
                // Clear saved form data after successful creation
                try {
                    sessionStorage.removeItem('pipeline-creation-form');
                } catch (error) {
                    console.error('Error clearing saved form data:', error);
                }
                
                // Wait a moment for user feedback, then navigate
                setTimeout(() => {
                    navigate(`/research-pipeline/${response.data.pipeline.uuid}`);
                }, 1500);
            } else {
                throw new Error(response.data.message || t('ResearchPipeline.failedCreatePipeline'));
            }
        } catch (error) {
            console.error('Error creating pipeline:', error);
            
            if (error.response?.status === 403) {
                setShowInsufficientCreditsModal(true);
            } else if (error.response?.data?.errors) {
                // Handle validation errors
                const errors = error.response.data.errors;
                const errorMessage = Object.values(errors).flat().join('\n');
                alert(`Validation errors:\n${errorMessage}`);
            } else {
                alert(t('ResearchPipeline.failedCreatePipelineTryAgain'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/research-pipeline');
    };

    return (
        <div className="md:pl-64 py-2 md:py-12 w-full flex flex-col gap-4 items-center px-4">
            <div className="flex flex-col w-full justify-center items-center gap-8 md:px-4">
                
                {/* Header Section */}
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl px-2">
                    <SectionHeader
                        title={t('ResearchPipeline.createPipeline')}
                        subtitle={t('ResearchPipeline.createResearchPipelineSubtitle')}
                        breadcrubs={[
                            { name: t('ResearchPipeline.breadcrumb'), href: "/research-pipeline" },
                            { name: t('ResearchPipeline.createPipeline'), href: "/research-pipeline/create" }
                        ]}
                        leftComponent={
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
{t('ResearchPipeline.backToPipelines')}
                            </button>
                        }
                        rightComponent={
                            <div className="text-sm text-gray-600">
                                {t('ResearchPipeline.availableCredits')}: <span className="font-semibold">{credits?.toFixed(2) || '0.00'}</span>
                            </div>
                        }
                    />
                </div>

                {/* Main Content */}
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <ParticleLoader size={48} />
                        </div>
                    ) : (
                        <div>
                            <div className="mb-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                    {t('ResearchPipeline.createResearchPipelineTitle')}
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    {t('ResearchPipeline.createResearchPipelineSubtitle')}
                                </p>
                            </div>
                            <PipelineCreationForm
                                projects={projects}
                                onSubmit={handleFormSubmit}
                                disabled={loading}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showCostModal && (
                <CostEstimationModal
                    costEstimate={costEstimate}
                    onConfirm={handleCostConfirmation}
                    onCancel={() => setShowCostModal(false)}
                />
            )}

            {showInsufficientCreditsModal && (
                <NoCreditsModal
                    title={t('CreditsModal.Title')}
                    body={t('CreditsModal.Body')}
                    continue={t('CreditsModal.Continue')}
                    navLink="/pricing/credits"
                    handleClose={() => setShowInsufficientCreditsModal(false)}
                />
            )}
        </div>
    );
}

export default CreateResearchPipeline;